import { ref } from 'vue';
import axios from 'axios';
import { User } from '@/types/types';
import { getAuth } from 'firebase/auth';
import { handleAxiosError } from './errorHandler';

import { API_BASE } from './apiBase';
const API_BASE_URL = `${API_BASE}/v2`;

/**
 * Update the current agent's profile (phone, name)
 */
export async function updateAgent(data: { phone?: string; name?: string }) {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();

  if (!idToken) {
    throw new Error('Not authenticated');
  }

  const response = await axios.put(`${API_BASE_URL}/agent`, data, {
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  });

  return response.data;
}

/**
 * Join a tenant by ID (used when user visits a page with ?j=<tenantId>)
 * Optionally pass an invite code for secure joining via ?ic=<code>
 */
export async function joinTenant(
  tenantId: string,
  inviteCode?: string
): Promise<{
  success: boolean;
  alreadyMember?: boolean;
  joinMethod?: string;
  tenantName?: string;
  code?: string;
  message?: string;
}> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();

  if (!idToken) {
    throw new Error('Not authenticated');
  }

  const body: { tenantId: string; inviteCode?: string } = { tenantId };
  if (inviteCode) {
    body.inviteCode = inviteCode;
  }

  const response = await axios.post(`${API_BASE_URL}/agent/join-tenant`, body, {
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  });

  return response.data;
}

/**
 * Generate (or regenerate) a secure invite code for a project
 */
export async function generateInviteCode(projectId: string): Promise<{
  inviteCode: string;
  createdAt: string;
}> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();

  if (!idToken) {
    throw new Error('Not authenticated');
  }

  const response = await axios.post(
    `${API_BASE_URL}/project/${projectId}/invite-code`,
    {},
    {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    }
  );

  return response.data;
}

/**
 * Get the current invite code for a project (if one exists)
 */
export async function getInviteCode(projectId: string): Promise<{
  hasCode: boolean;
  inviteCode?: string;
  createdAt?: string;
}> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();

  if (!idToken) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get(
    `${API_BASE_URL}/project/${projectId}/invite-code`,
    {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    }
  );

  return response.data;
}

export function useUsers() {
  const users = ref<User[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  async function listUsers(pageSize: number) {
    loading.value = true;
    error.value = null;
    try {
      const response = await axios.get<User[]>(`${API_BASE_URL}/user/list`, {
        params: { pageSize }
      });

      users.value = response.data;
    } catch (err) {
      error.value = err as Error;
    } finally {
      loading.value = false;
    }
  }

  const searchUsers = async (query: string, projectId: string) => {
    loading.value = true;
    error.value = null;
    const auth = getAuth();
    const idToken = await auth.currentUser?.getIdToken();

    try {
      const response = await axios.get(
        `${API_BASE_URL}/project/${projectId}/users`,
        {
          params: { query },
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        }
      );
      users.value = response.data.users;
    } catch (e) {
      error.value = e as Error;
      handleAxiosError(e);
    } finally {
      loading.value = false;
    }
  };

  async function exportUser() {
    const response = await axios.get(`${API_BASE_URL}/user/export`);
    return response.data;
  }

  async function addUser(user: User) {
    const response = await axios.post(`${API_BASE_URL}/user/add`, user);
    return response.data;
  }

  async function sendEmail(
    projectId: string,
    emailData: {
      recipients: string[];
      subject: string;
      messageBody: string;
      natural_language?: string;
    }
  ) {
    const auth = getAuth();
    const idToken =
      (await auth.currentUser?.getIdToken()) ||
      sessionStorage.getItem('idToken');

    try {
      const responses = await Promise.all(
        emailData.recipients.map(async (recipientId) => {
          const response = await axios.post(
            `${API_BASE_URL}/project/${projectId}/users/${recipientId}/communication/email`,
            {
              subject: emailData.subject,
              messageBody: emailData.messageBody,
              natural_language: emailData.natural_language
            },
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json'
              }
            }
          );
          return response;
        })
      );

      const successfulResponses = responses.filter(
        (response) => response.status === 200
      );
      if (successfulResponses.length === responses.length) {
        console.log('All emails sent successfully');
        return successfulResponses.map((response) => ({
          ...response.data
        }));
      } else {
        console.error('Some emails failed to send');
        return null;
      }
    } catch (error) {
      console.error('Error sending email:', error);
      handleAxiosError(error);
      return null;
    }
  }

  async function sendMessage(userId: string, messageBody: string) {
    const response = await axios.post(`${API_BASE_URL}/communication/message`, {
      userId,
      messageBody
    });
    return response.data;
  }

  async function editUser(userId: string, user: Partial<User>) {
    const response = await axios.put(`${API_BASE_URL}/users/edit`, {
      userId,
      ...user
    });
    return response.data;
  }

  async function deleteUser(userId: string) {
    const response = await axios.delete(`${API_BASE_URL}/users/delete`, {
      data: { userId }
    });
    return response.data;
  }

  async function getUserDetails(userId: string) {
    const response = await axios.get<User>(`${API_BASE_URL}/user/details`, {
      params: { userId }
    });
    return response.data;
  }

  async function getMessages(projectId: string, type: string) {
    const auth = getAuth();
    const idToken = await auth.currentUser?.getIdToken();

    try {
      const response = await axios.get(
        `${API_BASE_URL}/project/${projectId}/messages`,
        {
          params: { type },
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      handleAxiosError(error);
      return null;
    }
  }

  const getMessagesPerCustomer = async (projectId: string) => {
    const auth = getAuth();
    const idToken = await auth.currentUser?.getIdToken();

    try {
      const response = await axios.get(
        `${API_BASE_URL}/project/${projectId}/messages-per-customer`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching messages per customer:', error);
      handleAxiosError(error);
      return null;
    }
  };

  const getCustomers = async (projectId: string, query = '') => {
    const auth = getAuth();
    const idToken = await auth.currentUser?.getIdToken();

    try {
      const response = await axios.get(
        `${API_BASE_URL}/project/${projectId}/customers`,
        {
          params: { query },
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        }
      );
      return response.data.customers;
    } catch (error) {
      console.error('Error fetching customers:', error);
      handleAxiosError(error);
      return [];
    }
  };

  return {
    users,
    loading,
    error,
    listUsers,
    searchUsers,
    exportUser,
    addUser,
    sendEmail,
    sendMessage,
    editUser,
    deleteUser,
    getUserDetails,
    getMessages,
    getMessagesPerCustomer,
    getCustomers
  };
}
